import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Divider,
    Button,
    List,
    ListItem,
    ListItemText,
    Chip,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TranslateIcon from '@mui/icons-material/Translate';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GridViewIcon from '@mui/icons-material/GridView';

const SuggestionItem = ({ text, onClick }) => (
    <Box 
        sx={{ 
            p: 1.5, 
            mb: 1, 
            bgcolor: 'white', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            '&:hover': {
                borderColor: '#3b82f6',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }
        }}
        onClick={() => onClick(text)}
    >
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#94a3b8', mt: 1, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#1e293b', lineHeight: 1.5 }}>
            {text}
        </Typography>
    </Box>
);

const TermMatchItem = ({ source, target, onClick }) => (
    <Box 
        sx={{ 
            p: 1.5, 
            mb: 1, 
            bgcolor: 'white', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: '#3b82f6',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }
        }}
        onClick={() => onClick(target)}
    >
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <LibraryBooksIcon sx={{ fontSize: 16, color: '#64748b' }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>
                {source}
            </Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#3b82f6', ml: 3 }}>
            {target}
        </Typography>
    </Box>
);

const SmartSuggestions = ({
    isOpen,
    onClose,
    focusedSegment,
    originalText,
    suggestions = {
        memory: [],
        glossary: [],
        ai: null,
        history: []
    },
    onInsert,
    onRevealAI,
    isGeneratingAI = false
}) => {
    if (!isOpen) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                height: '100%',
                borderLeft: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#f8fafc',
                position: 'sticky',
                top: '0', 
                maxHeight: '100vh',
                overflowY: 'auto'
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Suggestions</Typography>
                <IconButton size="small" onClick={onClose}>
                    <ChevronRightIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                </IconButton>
            </Box>

            <Box sx={{ p: 2 }}>
                {focusedSegment === null ? (
                    <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                        <Typography variant="body2">Click a segment to see suggestions</Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {/* AI Suggestions Accordion */}
                        <Accordion defaultExpanded elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                            <AccordionSummary 
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                                sx={{ p: 0, minHeight: 0, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <AutoAwesomeIcon sx={{ fontSize: 12, color: 'white' }} />
                                    </Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        AI Suggestions
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pt: 1 }}>
                                {suggestions.ai ? (
                                    <SuggestionItem text={suggestions.ai} onClick={onInsert} />
                                ) : (
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: '8px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#64748b', fontStyle: 'italic' }}>
                                            Unlock AI assistance for this segment
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            fullWidth
                                            disableElevation
                                            onClick={onRevealAI}
                                            disabled={isGeneratingAI}
                                            startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                            sx={{ borderRadius: '6px', textTransform: 'none', bgcolor: '#3b82f6' }}
                                        >
                                            {isGeneratingAI ? 'Generating...' : 'Reveal Suggestion'}
                                        </Button>
                                    </Box>
                                )}
                                
                                {suggestions.memory && suggestions.memory.map((match, idx) => (
                                    <Box key={`mem-${idx}`} sx={{ mt: 1 }}>
                                        <SuggestionItem text={match.translatedText} onClick={onInsert} />
                                        <Typography variant="caption" sx={{ ml: 2, color: '#22c55e', fontWeight: 600 }}>
                                            {match.confidence}% Match
                                        </Typography>
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>

                        {/* Term Matches Accordion */}
                        <Accordion defaultExpanded elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                            <AccordionSummary 
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                                sx={{ p: 0, minHeight: 0, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <GridViewIcon sx={{ fontSize: 12, color: 'white' }} />
                                    </Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Term Matches
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pt: 1 }}>
                                {suggestions.glossary && suggestions.glossary.length > 0 ? (
                                    suggestions.glossary.map((term, idx) => (
                                        <TermMatchItem key={`term-${idx}`} source={term.source} target={term.target} onClick={onInsert} />
                                    ))
                                ) : (
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                        No glossary terms found
                                    </Typography>
                                )}

                                {suggestions.history && suggestions.history.map((item, idx) => (
                                    <TermMatchItem key={`hist-${idx}`} source={item.source} target={item.target} onClick={onInsert} />
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

export default SmartSuggestions;
